import {OrderDetailsAction} from './OrderDetailsActions'
import {OrderDetailsState} from './OrderDetailsState'
import * as OrderResource from '../../Request/OrderResource'
import * as WebData from '../../Data/WebData'
import {
    OrderDetailsDownloadRequestIdentifier,
    OrderDetailsFeedback,
    OrderDetailsRequestIdentifier
} from './OrderDetailsFeedback'
import {
    OrderWithDetails,
    orderWithDetailsDecoder
} from '../../Entities/OrderWithDetails'
import * as InvoiceAddress from '../../Entities/InvoiceAddress'
import * as OrderGeneralInformation from '../../Entities/OrderGeneralInformation'
import * as DeliveryAddress from '../../Entities/DeliveryAddress'
import {update} from '../../Data/ObjectHelper'
import * as Result from '../../Data/Result'
import {decodeHttpResponse, HttpError} from '../../Data/Http'
import {initPositions, positionsReducer} from './Positions/PositionsReducer'
import {initInvoice, invoiceReducer} from './Invoice/InvoiceReducer'
import {deliveryReducer, initDelivery} from './Delivery/DeliveryReducer'
import {
    generalInformationReducer,
    initGeneralInformation
} from './GeneralInformation/GeneralInformationReducer'
import {initParcels, parcelsReducer} from './Parcels/ParcelsReducer'
import * as ParcelDispatcherResource from '../../Request/ParcelDispatcherResource'
import {array, Decoder} from 'json-bouncer'
import {parcelDispatcherDecoder} from '../../Entities/ParcelDispatcher'
import {Session} from '../../Data/Session'
import {productFinderHttpResponseAction} from '../../Components/ProductFinder/ProductFinderReducer'
import {FeedbackLoop} from '../../Data/FeedbackLoop'
import {HttpRequest} from '../../Data/HttpRequest'

type OrderDetailsLoop =
    FeedbackLoop<OrderDetailsState, OrderDetailsFeedback>

export function activate(session: Session, orderNumber: string): HttpRequest {
    return OrderResource.retrieveWithDetails(
        session.manufacturer.id,
        orderNumber,
    )
}

export function initialDecoder(): Decoder<OrderWithDetails> {
    return orderWithDetailsDecoder()
}

export function init(
    session: Session,
    orderWithDetails: OrderWithDetails
): OrderDetailsLoop {
    return {
        state: {
            editDetails: false,
            activeTab: 'GeneralInformation',
            orderWithDetails,
            generalInformationTab:
                initGeneralInformation(
                    orderWithDetails.order.orderPaid,
                    OrderGeneralInformation.fromOrder(
                        orderWithDetails.order,
                    ),
                ),
            positionsTab:
                initPositions(orderWithDetails.positionsWithProducts),
            invoiceTab:
                initInvoice(
                    InvoiceAddress.fromOrder(
                        orderWithDetails.order)),
            deliveryTab:
                initDelivery(
                    DeliveryAddress.fromOrder(
                        orderWithDetails.order)),
            parcelsTab:
                initParcels(
                    orderWithDetails
                        .productShippingServicesForOrder),
            parcelDispatchers: {type: 'Loading'},
        },
        feedback: [
            {
                type: 'SendRequest',
                identifier: {
                    type: 'ParcelDispatcherList',
                },
                httpRequest: ParcelDispatcherResource.list(),
            },
        ]
    }
}

export function reduce(
    session: Session,
    action: OrderDetailsAction,
    state: OrderDetailsState
): OrderDetailsLoop {
    switch (action.type) {
        case 'ActiveTabChanged':
            return {
                state: update({
                    activeTab: action.value,
                }, state),
                feedback: [],
            }
        case 'ParcelDispatcherListResponse': {
            const result =
                decodeHttpResponse(
                    array(parcelDispatcherDecoder()),
                    action.result)
            return {
                state: update({
                    parcelDispatchers:
                        WebData.fromResult(result),
                }, state),
                feedback: [],
            }
        }
        case 'MenuButtonClicked': {
            return {
                state,
                feedback: [{type: 'OpenDrawer'}],
            }
        }
        case 'GeneralInformationAction': {
            const loop = generalInformationReducer(
                state.orderWithDetails.order.id,
                state.generalInformationTab,
                action.subAction
            )
            return {
                state: update({generalInformationTab: loop.state}, state),
                feedback: loop.feedback,
            }
        }
        case 'PositionsAction': {
            const loop = positionsReducer(
                session,
                state.orderWithDetails.order.customerId,
                state.orderWithDetails.order.id,
                action.subAction,
                state.positionsTab,
            )
            return {
                state: update({positionsTab: loop.state}, state),
                feedback: loop.feedback,
            }
        }
        case 'InvoiceAction': {
            const loop = invoiceReducer(
                state.orderWithDetails.order.id,
                action.subAction,
                state.invoiceTab
            )
            return {
                state: update({invoiceTab: loop.state}, state),
                feedback: loop.feedback,
            }
        }
        case 'DeliveryAction': {
            const loop = deliveryReducer(
                state.orderWithDetails.order.id,
                action.subAction,
                state.deliveryTab
            )
            return {
                state: update({deliveryTab: loop.state}, state),
                feedback: loop.feedback,
            }
        }
        case 'ParcelsAction': {
            const loop = parcelsReducer(state.parcelsTab, action.subAction)
            return {
                state: update({parcelsTab: loop.state}, state),
                feedback: loop.feedback,
            }
        }
    }
}

export function httpResponseAction(
    identifier: OrderDetailsRequestIdentifier,
    result: Result.Result<HttpError, string>
): OrderDetailsAction {
    switch (identifier.type) {
        case 'OrderGeneralInfoPatch':
            return {
                type: 'GeneralInformationAction',
                subAction: {
                    type: 'OrderGeneralInfoPatchResponse',
                    result,
                }
            }
        case 'PositionBatch':
            return {
                type: 'PositionsAction',
                subAction: {
                    type: 'PositionBatchResponse',
                    result,
                }
            }
        case 'OrderInvoiceAddressPatch':
            return {
                type: 'InvoiceAction',
                subAction: {
                    type: 'InvoiceAddressPatchResponse',
                    result,
                },
            }
        case 'OrderDeliveryAddressPatch':
            return {
                type: 'DeliveryAction',
                subAction: {
                    type: 'DeliveryInformationUpdateResponse',
                    result,
                },
            }
        case 'OrderPaidPatch':
            return {
                type: 'GeneralInformationAction',
                subAction: {
                    type: 'OrderPaidResponse',
                    orderId: identifier.orderId,
                    result,
                }
            }
        case 'ParcelDispatcherList':
            return {
                type: 'ParcelDispatcherListResponse',
                result,
            }
        case 'ParcelCreate':
            return {
                type: 'ParcelsAction',
                subAction: {
                    type: 'ParcelCreateResponse',
                    productShippingServiceId: identifier.productShippingServiceId,
                    result,
                }
            }
        case 'ParcelDelete':
            return {
                type: 'ParcelsAction',
                subAction: {
                    type: 'ParcelDeleteResponse',
                    productShippingServiceId: identifier.productShippingServiceId,
                    parcelId: identifier.parcelId,
                    result,
                }
            }
        case 'ProductFinderModuleRequest':
            return {
                type: 'PositionsAction',
                subAction: {
                    type: 'ProductFinderModuleAction',
                    subAction:
                        productFinderHttpResponseAction(
                            identifier.identifier,
                            result,
                        ),
                },
            }
    }
}

export function httpDownloadResponseAction(
    identifier: OrderDetailsDownloadRequestIdentifier,
    result: Result.Result<HttpError, null>,
): OrderDetailsAction {
    switch (identifier.type) {
        case 'ProductPrintPdfDownload':
            return {
                type: 'PositionsAction',
                subAction: {
                    type: 'ProductPrintPdfDownloadResponse',
                    id: identifier.id,
                    result,
                },
            }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Pages/OrderDetails/OrderDetailsReducer.ts